// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageWarning, messageBoxText } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import TypeVariablesEdit from './Edit';

import { typeSensorVariable, TypeSensorVariableDO } from '../../../requests';

interface Props {
  rowId: number;
}

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: TypeSensorVariableDO[];
}

export default class List extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private buttons: TableButtonsConfig;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      add: hasLevel('role', ['ADM']),
      del: hasLevel('role', ['ADM']),
      edit: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      data: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load();
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    const { rowId } = this.props;

    this.loadingStart();
    typeSensorVariable<TypeSensorVariableDO[]>('list', undefined, { typeSensorId: rowId })
      ?.then((resp: TypeSensorVariableDO[]) => {
        this.setLocalState({ data: resp });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const { rowId: typeSensorId } = this.props;

    const tabId: string = 'STVE';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: `${rowId ? `Variable Edit: ${rowId}` : 'Variable New'} | Sensor-Type ID: ${typeSensorId}`,
      component: TypeVariablesEdit,
      props: {
        rowId,
        tabId: this.pid,
        report: 'TYPES VARIABLES EDIT',
        title: 'Types Variables Edit',
        notify: this.notify,
        typeSensorId,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (item: TypeSensorVariableDO): void => {
    const rowId: number = item?.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (item: TypeSensorVariableDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.id}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        typeSensorVariable('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load();
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      order: 'Order',
      variableName: 'Variable',
      bytes: 'Bytes',
      formula: 'Formula',
      discardMin: 'Discard min',
      discardMax: 'Discard max',
      adjustMin: 'Adjust min',
      adjustMax: 'Adjust max',
      variableUnit: 'Unit',
    };
    const columnsToHide: string[] = [
      'variableId',
      'typeSensorName',
      'typeSensorId'
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      order: { textAlign: 'right' },
      bytes: { textAlign: 'right' },
      formula: { textAlign: 'left' },
      discardMin: { textAlign: 'right' },
      discardMax: { textAlign: 'right' },
      adjustMin: { textAlign: 'right' },
      adjustMax: { textAlign: 'right' },
      variableName: { textAlign: 'left' },
      variableUnit: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
          pagination={false}
          showRowNumber
        />
      </div>
    );
  }
}
